import {
  Fragment,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {Trash2, Copy, ChevronUp, ChevronDown, LayoutTemplate, PanelTop} from 'lucide-react';
import type {PhotoRequirement, TemplateSettings} from '../lib/types';
import type {RichText} from '../lib/rich-text';
import {formatHeaderScope} from '../lib/header-scope';
import {SELECTION_PALETTE} from '../lib/selection-palette';
import {PreviewElement} from './PreviewElement';
import {FlowDropIndicator, ResizeHandles, frameBorderFor} from './LivePreviewParts';
import {PageNumberStamps} from './PageNumberStamps';
import {ElementContextMenu, type ZOrderAction} from './ElementContextMenu';
import {
  A4_W,
  A4_H,
  PAGE_GAP,
  PAGE_CENTER_X,
  FLOW_GAP,
  FLOW_WIDTH_MAP,
} from '../lib/preview-geometry';
import {useAbsoluteDrag, useResize, useMarquee} from '../hooks/useCanvasDrag';
import {usePreviewLayout} from '../hooks/usePreviewLayout';
import {usePreviewEditing} from '../hooks/usePreviewEditing';
import {useFlowDrag} from '../hooks/useFlowDrag';
import type {PhotoPick} from '@/lib/photo-capture/image-input';
import type {FillAudience} from '../lib/client-fillable';
import {FillSurfaceProvider} from './FillSurface';
import {inertControls} from '../lib/inert-controls';
import {HeaderBandMark, MarqueeBox, SelectionBox, SnapGuide} from './CanvasOverlays';
import {laysItselfOut} from '../lib/element-chrome';

// ── Public handle exposed via forwardRef ──────────────────────────────────────

export interface LivePreviewHandle {
  getElementBounds: (id: string) => {x: number; y: number; width: number; height: number} | null;
  getPageGroups: () => {itemIds: string[]; pageBreakId?: string}[];
}

interface Props {
  requirements: PhotoRequirement[];
  settings: Partial<TemplateSettings>;
  fillMode?: boolean;
  sampleData?: Record<string, string>;
  /**
   * Interactive-fill plumbing (keyed by element id). When provided alongside
   * `fillMode`, the input elements capture the worker's answers. `fillValues`
   * holds the current answer per element (photos as their stored URL string);
   * `onFillChange` records scalar/checkbox/signature edits; `onFillPhoto`
   * hands a captured file to the parent to upload.
   */
  fillValues?: Record<string, unknown>;
  onFillChange?: (id: string, value: unknown) => void;
  /** `aspect` is the slot's rendered width ÷ height — see `ElemProps.onFillPhoto`. */
  onFillPhoto?: (id: string, pick: PhotoPick, aspect?: number) => void;
  /** Show captured fill values but disable editing (completed-report view). */
  fillReadOnly?: boolean;
  /**
   * Which slots THIS filler may answer, by fill key. Omitted means all of them.
   *
   * A report has two halves and one document (DERP-247): the public signing link
   * shows the whole thing — the crew's answers included, because a signer must
   * see what they are signing — but only the client's own slots are theirs to
   * touch. Read-only is therefore a per-slot answer, not a page-wide one, and it
   * is decided here because `fillProps` is already the single place a slot's
   * plumbing is scoped to its id.
   */
  fillableKeys?: readonly string[];
  /**
   * Whose half of the document this surface is showing (DERP-247) — the crew's
   * canvas, or the client's own on a public signing link. Decides what a
   * client-owned slot is CALLED to the person looking at it; the wording lives
   * with the component that draws it. Defaults to the crew, which is what an
   * editor preview is.
   */
  audience?: FillAudience;
  /**
   * This canvas is being rasterised to PDF rather than shown in the app
   * (DERP-237). Drops the chrome that is ours rather than the document's — see
   * `FillSurface.forExport`.
   */
  forExport?: boolean;
  /** Signer-name choices for signature fields (the job's assignees). */
  signerOptions?: readonly string[];
  defaultSignerName?: string;
  zoom?: number;
  /** Pages per row in the wrapping multi-page layout (1 = single vertical column). */
  pagesPerRow?: number;
  selectedIds?: string[];
  /** Currently-open group sentinel id (group edit mode) */
  groupEditId?: string | null;
  onElementMove?: (moves: {id: string; x: number; y: number}[]) => void;
  onElementResize?: (id: string, x: number, y: number, width: number, height: number) => void;
  onElementClick?: (id: string, opts?: {additive?: boolean}) => void;
  onSelectionChange?: (ids: string[], opts?: {additive?: boolean}) => void;
  onDeselect?: () => void;
  onSelectHeaderGroup?: () => void;
  onDeletePage?: (elementIds: string[], pageIdx: number) => void;
  /** Duplicate every element on `pageIdx` as a new page inserted directly below it. */
  onDuplicatePage?: (elementIds: string[], pageIdx: number) => void;
  /** Move the page at `pageIdx` up (-1) or down (+1), swapping it with its neighbour. */
  onMovePage?: (pageIdx: number, dir: -1 | 1) => void;
  onLabelChange?: (id: string, label: RichText) => void;
  onElementChange?: (updated: PhotoRequirement) => void;
  activePageIndex?: number;
  onPageFocus?: (pageIdx: number) => void;
  /** Reorder flow elements via drag-and-drop. `movingIds` move as one block to
   *  sit before/after `target.refId` in the flow sequence. */
  onReorderFlow?: (
    movingIds: string[],
    target: {refId: string; placement: 'before' | 'after'},
  ) => void;
  /** Change an absolute element's stacking order (Canva-style layering) via the
   *  right-click menu. Reorders the element's `order` among the page's abs items. */
  onReorderZ?: (id: string, action: ZOrderAction) => void;
}

export const LivePreview = forwardRef<LivePreviewHandle, Props>(function LivePreview(
  {
    requirements,
    settings,
    fillMode = false,
    sampleData = {},
    fillValues,
    onFillChange,
    onFillPhoto,
    fillReadOnly,
    fillableKeys,
    audience,
    forExport,
    signerOptions,
    defaultSignerName,
    zoom = 1,
    pagesPerRow = 1,
    selectedIds,
    groupEditId,
    onElementMove,
    onElementResize,
    onElementClick,
    onSelectionChange,
    onDeselect,
    onSelectHeaderGroup,
    onDeletePage,
    onDuplicatePage,
    onMovePage,
    onLabelChange,
    onElementChange,
    activePageIndex,
    onPageFocus,
    onReorderFlow,
    onReorderZ,
  },
  ref,
) {
  const page = settings?.page;
  const marginT = page?.margin_top ?? 40;
  const marginB = page?.margin_bottom ?? 40;
  const marginL = page?.margin_left ?? 40;
  const marginR = page?.margin_right ?? 40;

  // ── Inline editing ────────────────────────────────────────────────────────────
  // Inline label + photo-crop editing.
  const {
    inlineEdit,
    inlineEditId,
    startInlineEdit,
    cropEditId,
    startEditOrCrop,
    handleCropApply,
    handleCropCancel,
  } = usePreviewEditing({fillMode, requirements, onLabelChange, onElementChange});

  // Right-click layering menu for an absolute element (Canva-style z-order).
  const [zMenu, setZMenu] = useState<{
    x: number;
    y: number;
    id: string;
    atFront: boolean;
    atBack: boolean;
  } | null>(null);

  // ── Element refs ──────────────────────────────────────────────────────────────
  const elemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // One ref per rendered A4 page div — used by getElementBounds to measure flow items.
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Ref to latest pageGroups — written after pageGroups is computed, read by the handle.
  const pageGroupsRef = useRef<{items: PhotoRequirement[]; pageBreakId?: string}[]>([]);

  // ── Expose handle ─────────────────────────────────────────────────────────────
  useImperativeHandle(
    ref,
    () => ({
      getElementBounds: (id: string) => {
        const req = requirements.find((r) => r.id === id);
        if (!req) return null;
        const el = elemRefs.current.get(id);
        if (!el) return null;

        // Absolute elements: stored x/y is canonical.
        if (req.position_mode === 'absolute') {
          return {
            x: req.x ?? 0,
            y: req.y ?? 0,
            width: req.width ?? el.offsetWidth,
            height: req.height ?? el.offsetHeight,
          };
        }

        // Flow elements: derive position from DOM (x/y are not stored).
        for (const [pageIdx, pageDiv] of pageRefs.current) {
          if (pageDiv.contains(el)) {
            const pageRect = pageDiv.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            return {
              x: Math.round((elRect.left - pageRect.left) / zoom),
              y: Math.round(pageIdx * A4_H + (elRect.top - pageRect.top) / zoom),
              width: Math.round(elRect.width / zoom),
              height: Math.round(elRect.height / zoom),
            };
          }
        }
        return null;
      },
      getPageGroups: () =>
        pageGroupsRef.current.map((g) => ({
          itemIds: g.items.map((i) => i.id),
          pageBreakId: g.pageBreakId,
        })),
    }),
    [requirements, zoom],
  );

  // ── Sentinel ids & map (group metadata elements, type === "group") ────────────
  // Sentinels are NOT containers — they are invisible metadata elements.
  // Children keep their own position_mode and page-level x/y coordinates.
  // The sentinel only stores group label, position_mode (informational), and a
  // reference bounding box that is updated as children move.
  // Paginated layout (flow/header/absolute lists + page groups + measurement).
  const {
    pageNumberStamps,
    sentinelIds,
    sentinelMap,
    flowItems,
    absoluteItems,
    headerRegions,
    headerForPage,
    regionHeight,
    pageGroups,
    orderedFlowRenderIds,
    absoluteByPage,
    absoluteSentinelsByPage,
    totalPages,
    measureRefs,
  } = usePreviewLayout({requirements, marginT, marginB, fillMode});

  // ── Drag context + interaction hooks ──────────────────────────────────────────
  // Always-current ref so the drag handlers never read stale closure values when
  // selectedIds / groupEditId / sentinelMap change between renders.
  const dragCtxRef = useRef({selectedIds, groupEditId, sentinelIds, sentinelMap});
  useEffect(() => {
    dragCtxRef.current = {selectedIds, groupEditId, sentinelIds, sentinelMap};
  });

  const {dragState, startDrag} = useAbsoluteDrag({
    elemRefs,
    dragCtxRef,
    requirements,
    zoom,
    fillMode,
    onElementMove,
    onElementClick,
  });
  const {resizeState, startResize} = useResize({elemRefs, zoom, fillMode, onElementResize});
  const {marquee, startMarquee} = useMarquee({
    elemRefs,
    zoom,
    fillMode,
    requirements,
    onSelectionChange,
    onPageFocus,
  });

  // Keep the ref the imperative handle reads in sync with the computed groups.
  useEffect(() => {
    pageGroupsRef.current = pageGroups;
  }, [pageGroups]);

  const draggingPageIdx = dragState ? Math.max(0, Math.floor((dragState.y ?? 0) / A4_H)) : -1;

  const canEdit = !fillMode && (!!onElementMove || !!onElementResize);
  const canInteract = !fillMode;
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Per-slot interactive-fill props (no-ops unless the parent wired fill).
  // Spread into a normal element for its own id; handed down WHOLE to a
  // container element, which scopes it per child slot itself (DERP-225).
  const fillable = useMemo(() => (fillableKeys ? new Set(fillableKeys) : null), [fillableKeys]);
  const fillProps = useCallback(
    (id: string) => {
      // Not mine to fill reads exactly like read-only, and must ALSO withhold the
      // callbacks: a renderer treats "has onFillChange" as "is an input", so
      // leaving them wired would render an editable control that silently
      // discarded what was typed.
      const notMine = fillable !== null && !fillable.has(id);
      const locked = Boolean(fillReadOnly) || notMine;
      return {
        fillValue: fillValues?.[id],
        onFillChange: onFillChange && !locked ? (v: unknown) => onFillChange(id, v) : undefined,
        onFillPhoto:
          onFillPhoto && !locked
            ? (pick: PhotoPick, aspect?: number) => onFillPhoto(id, pick, aspect)
            : undefined,
        fillReadOnly: locked,
        /**
         * Locked because it is the OTHER half's, on a document that is still
         * open — as opposed to `fillReadOnly`, which is the document being
         * settled.
         *
         * The two used to collapse into one flag, and a renderer that cannot
         * tell them apart cannot draw one document: a client's signature name
         * came out as an input on the signing link and a dashed line on the job,
         * two different heights, so the same JAC paginated differently on the
         * two screens. What a slot IS does not depend on who is looking at it.
         */
        fillLocked: notMine && !fillReadOnly,
      };
    },
    [fillValues, onFillChange, onFillPhoto, fillReadOnly, fillable],
  );

  // ── Group context helper ───────────────────────────────────────────────────────
  // Computes the three group-mode flags for any child element.
  // `sentinelGroupId`: the sentinel id if this item belongs to a sentinel group.
  // `inGroupEditMode`: the group is open for individual child editing.
  // `inGroupSelectionMode`: the group is selected as a whole unit.
  const groupContext = useCallback(
    (item: PhotoRequirement) => {
      const sentinelGroupId =
        item.group_id != null && sentinelIds.has(item.group_id) ? item.group_id : null;
      const inGroupEditMode = sentinelGroupId != null && groupEditId === sentinelGroupId;
      const inGroupSelectionMode =
        sentinelGroupId != null &&
        !inGroupEditMode &&
        (selectedIds?.includes(sentinelGroupId) ?? false);
      return {sentinelGroupId, inGroupEditMode, inGroupSelectionMode};
    },
    [sentinelIds, groupEditId, selectedIds],
  );

  // ── Flow drag-and-drop reordering ───────────────────────────────────────────
  const {flowDrag, startFlowDrag} = useFlowDrag({
    requirements,
    orderedFlowRenderIds,
    elemRefs,
    dragCtxRef,
    groupContext,
    onReorderFlow,
    fillMode,
    inlineEditId,
  });

  // Render one absolute-positioned element. Shared by the header zone and the
  // page body — they differ only in: the y-offset applied to `top` (header-zone
  // vs page-local coordinates), whether the element registers a measurement ref,
  // an optional pre-drag hook (page focus), and an optional context menu (z-order,
  // page only). A render helper (not a component) so it reads the surrounding
  // state/callbacks directly without a large prop list or remounting on each render.
  const renderAbsoluteItem = (
    item: PhotoRequirement,
    opts: {
      yOffset: number;
      registerRef: boolean;
      onBeforeDrag?: () => void;
      onContextMenu?: (e: React.MouseEvent) => void;
    },
  ) => {
    const rs = resizeState?.id === item.id ? resizeState : null;
    const ds = dragState?.id === item.id ? dragState : null;
    const groupOffset = dragState?.groupOffsets?.get(item.id);

    const liveX = rs?.x ?? ds?.x ?? groupOffset?.x ?? item.x ?? 0;
    const liveY = rs?.y ?? ds?.y ?? groupOffset?.y ?? item.y ?? 0;
    const liveW = rs?.width ?? item.width ?? 'auto';
    const liveH = rs?.height ?? item.height ?? 'auto';
    const isDragging = !!ds || !!groupOffset;
    const isResizing = !!rs;
    const active = isDragging || isResizing;

    const {sentinelGroupId, inGroupEditMode, inGroupSelectionMode} = groupContext(item);
    const isSelected = selectedIds?.includes(item.id) ?? false;
    const hovered = hoveredId === item.id;
    const showFrame = canEdit && (hovered || active || isSelected || inGroupSelectionMode);
    const frameBorder = frameBorderFor({inGroupSelectionMode, active, isSelected, hovered});
    // A table draws its own frame, handles and grip — see `laysItselfOut`.
    const ownChrome = laysItselfOut(item);
    const itemInlineEdit = inGroupSelectionMode ? undefined : inlineEdit;

    return (
      <div
        key={item.id}
        // Names the element's own wrapper. The chrome around an absolute element
        // -- frame, clip, handles -- is all on this node and the one inside it,
        // and there was no way to speak about either except by their inline
        // styles, which is exactly the kind of target that changes meaning
        // without anyone editing it.
        data-element-id={item.id}
        ref={
          opts.registerRef
            ? (el) => {
                if (el) elemRefs.current.set(item.id, el);
                else elemRefs.current.delete(item.id);
              }
            : undefined
        }
        onMouseEnter={() => canEdit && setHoveredId(item.id)}
        onMouseLeave={() => setHoveredId(null)}
        onMouseDown={(e) => {
          opts.onBeforeDrag?.();
          startDrag(e, item);
        }}
        onContextMenu={opts.onContextMenu}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (inGroupSelectionMode) {
            onElementClick?.(sentinelGroupId!);
          } else if (!inGroupEditMode || isSelected) {
            startEditOrCrop(item);
          }
        }}
        style={{
          position: 'absolute',
          left: liveX,
          top: liveY - opts.yOffset,
          width: liveW,
          height: liveH,
          cursor: showFrame ? (isDragging ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none',
          zIndex: active ? 20 : 10,
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            // **An element that draws its own chrome draws it OUTSIDE its box.**
            // A table's corner squares straddle its corners and its move grip
            // sits clear of the edge, so clipping to the box cut the grip away
            // entirely and took half of every corner with it -- which is why an
            // absolute table appeared to have neither while a flow table, whose
            // wrapper does not clip, had both. Everything else keeps the clip:
            // it is what stops an element's content spilling across the page.
            overflow: ownChrome ? 'visible' : 'hidden',
            boxSizing: 'border-box',
            // The third outline path, and the last. A table is framed by its own
            // borders and the highlight on the cell under the pointer; the
            // canvas frame was a purple rectangle around all of it.
            border: showFrame && !ownChrome ? frameBorder : undefined,
          }}
        >
          <PreviewElement
            item={item}
            fillMode={fillMode}
            sampleData={sampleData}
            inlineEdit={itemInlineEdit}
            {...fillProps(item.id)}
            cellFill={fillProps}
            onChangeElement={onElementChange}
            onSelect={() => onElementClick?.(item.id)}
            cropEditId={cropEditId}
            zoom={zoom}
            onCropApply={handleCropApply}
            onCropCancel={handleCropCancel}
          />
        </div>
        <ResizeHandles
          show={
            canEdit &&
            isSelected &&
            !inGroupSelectionMode &&
            !ownChrome &&
            (active || (selectedIds?.length ?? 0) <= 1)
          }
          onResizeStart={(e, h) => startResize(e, item, h)}
        />
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <FillSurfaceProvider
      audience={audience}
      signerOptions={signerOptions}
      defaultSignerName={defaultSignerName}
      forExport={forExport}
    >
      <div style={{fontFamily: settings?.default_styles?.font_family ?? 'Inter, sans-serif'}}>
        {/* Hidden measurement pass */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            width: A4_W - marginL - marginR,
            top: 0,
            left: 0,
            zIndex: -1,
          }}
        >
          {flowItems
            .filter((i) => i.type !== 'page_break')
            .map((item) => (
              <div
                key={item.id}
                ref={(el) => {
                  if (el) measureRefs.current.set(item.id, el);
                  else measureRefs.current.delete(item.id);
                }}
              >
                <PreviewElement item={item} fillMode={false} sampleData={sampleData} />
              </div>
            ))}
        </div>

        {/* A4 pages. Laid out as a wrapping row grid: the parent sizes this container
            to fit exactly `pagesPerRow` pages, so flex-wrap breaks rows automatically.
            Multi-column wraps left-to-right; a lone column stays centered.
            The page-mapping callback reads elemRefs.current for a live width/height
            fallback while computing the multi-select bounding box — measured DOM is the
            source of truth and there is no render-state equivalent. */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: pagesPerRow > 1 ? 'flex-start' : 'center',
            gap: PAGE_GAP,
            // Breathing room below the last page/row so it isn't flush against the
            // scroll viewport's bottom edge. Lives inside the measured + scaled
            // content (not the scroll container) so it survives the zoom transform
            // and multi-page layouts — a trailing spacer/margin outside the scaled
            // content gets swallowed by the transformed scroll region.
            paddingBottom: 48,
          }}
        >
          {Array.from({length: totalPages}, (_, pageIdx) => {
            const group = pageGroups[pageIdx];
            const pageItems = group?.items ?? [];
            const pageAbsoluteItems = absoluteByPage.get(pageIdx) ?? [];
            // The header region (if any) active on this page, per its own scope.
            const pageHeader = headerForPage(pageIdx);
            const pageHeaderH = pageHeader ? regionHeight(pageHeader) : 0;
            const showHeaderHere = pageHeaderH > 0;
            const headerOffset = pageHeaderH;
            // The first page this header appears on — owns ref registration, the label,
            // and inline editing so a multi-page header doesn't duplicate them.
            const isHeaderOriginPage = pageHeader
              ? pageIdx === Math.max(0, pageHeader.scope.from - 1)
              : false;
            const headerScopeText = pageHeader ? formatHeaderScope(pageHeader.scope) : '';
            const isEmpty =
              pageIdx === 0 &&
              pageItems.length === 0 &&
              absoluteItems.length === 0 &&
              headerRegions.length === 0;

            // Multi-select bounding box (for non-sentinel absolute items)
            const selIds = selectedIds ?? [];
            const selAbsOnPage = pageAbsoluteItems.filter(
              (item) => selIds.includes(item.id) && item.type !== 'group',
            );
            let selBox: {minX: number; minY: number; maxX: number; maxY: number} | null = null;
            let selBoxLabel: string | undefined;

            if (selAbsOnPage.length >= 2) {
              const firstGroupId = selAbsOnPage[0]?.group_id;
              const allSameGroup =
                !!firstGroupId &&
                selAbsOnPage.every((item) => item.group_id === firstGroupId) &&
                !sentinelIds.has(firstGroupId); // only for old-style groups
              selBoxLabel = allSameGroup ? (selAbsOnPage[0]?.group_label ?? undefined) : undefined;
              for (const item of selAbsOnPage) {
                const ref = elemRefs.current.get(item.id);
                const w = item.width ?? ref?.offsetWidth ?? 100;
                const h = item.height ?? ref?.offsetHeight ?? 100;
                const x = item.x ?? 0;
                const y = (item.y ?? 0) - pageIdx * A4_H;
                if (!selBox) {
                  selBox = {minX: x, minY: y, maxX: x + w, maxY: y + h};
                } else {
                  selBox = {
                    minX: Math.min(selBox.minX, x),
                    minY: Math.min(selBox.minY, y),
                    maxX: Math.max(selBox.maxX, x + w),
                    maxY: Math.max(selBox.maxY, y + h),
                  };
                }
              }
            }

            return (
              <div key={pageIdx} style={{width: A4_W, flexShrink: 0}}>
                {/* Page label */}
                {!fillMode && (
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide transition-colors ${pageIdx === activePageIndex ? 'bg-indigo-100 text-indigo-700' : 'text-muted-foreground'}`}
                    >
                      Page {pageIdx + 1}
                    </span>
                    <div className="flex-1 border-t border-dashed border-border" />
                    {onMovePage && totalPages > 1 && (
                      <>
                        <button
                          className="text-muted-foreground transition-colors enabled:hover:text-indigo-400 disabled:opacity-30"
                          title={`Move page ${pageIdx + 1} up`}
                          disabled={pageIdx === 0}
                          onClick={() => onMovePage(pageIdx, -1)}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button
                          className="text-muted-foreground transition-colors enabled:hover:text-indigo-400 disabled:opacity-30"
                          title={`Move page ${pageIdx + 1} down`}
                          disabled={pageIdx === totalPages - 1}
                          onClick={() => onMovePage(pageIdx, 1)}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </>
                    )}
                    {onDuplicatePage && (
                      <button
                        className="text-muted-foreground hover:text-indigo-400 transition-colors"
                        title={`Duplicate page ${pageIdx + 1}`}
                        onClick={() => {
                          const duplicateIds = [
                            ...(group?.items.map((i) => i.id) ?? []),
                            ...(absoluteByPage.get(pageIdx)?.map((i) => i.id) ?? []),
                          ];
                          onDuplicatePage(duplicateIds, pageIdx);
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    )}
                    {onDeletePage && (
                      <button
                        className="text-muted-foreground hover:text-red-400 transition-colors"
                        title={`Delete page ${pageIdx + 1}`}
                        onClick={() => {
                          const deleteIds = [
                            ...(group?.items.map((i) => i.id) ?? []),
                            ...(group?.pageBreakId ? [group.pageBreakId] : []),
                            ...(absoluteByPage.get(pageIdx)?.map((i) => i.id) ?? []),
                          ];
                          onDeletePage(deleteIds, pageIdx);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}

                {/* A4 sheet */}
                {/* The A4 sheet is a WYSIWYG print surface: `bg-white text-black` is
                    intentionally fixed (not theme-aware) so what the user designs matches the
                    printed / exported PDF in either app theme. Its overlay chrome is likewise
                    fixed — see SELECTION_PALETTE. */}
                <div
                  data-report-sheet
                  ref={(el) => {
                    if (el) pageRefs.current.set(pageIdx, el);
                    else pageRefs.current.delete(pageIdx);
                  }}
                  // Names the sheet a table measures itself against: it is the
                  // thing that CLIPS, so it is the thing that decides whether a
                  // handle drawn outside an element is reachable or merely drawn.
                  data-page-sheet={pageIdx}
                  className={`relative bg-white text-black overflow-hidden rounded-[3px] transition-shadow shadow-[0_2px_8px_rgba(0,0,0,0.35),0_12px_32px_-6px_rgba(0,0,0,0.45)] ${!fillMode && pageIdx === activePageIndex ? 'ring-2 ring-indigo-400 ring-offset-4 ring-offset-muted' : 'ring-1 ring-border'}`}
                  style={{width: A4_W, height: A4_H}}
                  onClick={() => onDeselect?.()}
                  onMouseDown={(e) => startMarquee(e, pageIdx)}
                >
                  {draggingPageIdx === pageIdx && dragState?.showGuide && (
                    <SnapGuide x={PAGE_CENTER_X} />
                  )}

                  {marquee && marquee.pageIdx === pageIdx && <MarqueeBox rect={marquee} />}

                  {selBox && <SelectionBox box={selBox} label={selBoxLabel} />}

                  {/* Editor chrome, never the report's — see `CanvasOverlays`. */}

                  {showHeaderHere && canInteract && (
                    <HeaderBandMark height={marginT + pageHeaderH} />
                  )}

                  {/* Header zone — children positioned relative to this container */}
                  {showHeaderHere && pageHeader && (
                    <div
                      style={{
                        position: 'absolute',
                        top: marginT,
                        left: marginL,
                        right: marginR,
                        minHeight: pageHeaderH - FLOW_GAP,
                        zIndex: 5,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (fillMode) return;
                        if (pageHeader.isLegacy) onSelectHeaderGroup?.();
                        else onElementClick?.(pageHeader.id);
                      }}
                    >
                      {canInteract && isHeaderOriginPage && (
                        <div className="pointer-events-none absolute" style={{top: -18, left: 0}}>
                          <span className="inline-flex items-center gap-1 rounded-md border border-violet-300 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold leading-none text-violet-700 whitespace-nowrap shadow-sm">
                            <PanelTop className="h-2.5 w-2.5" />
                            {pageHeader.label}
                            <span className="font-normal text-violet-500">· {headerScopeText}</span>
                          </span>
                        </div>
                      )}

                      {/* Flow header items */}
                      {pageHeader.flow.length > 0 && (
                        <div className="space-y-3">
                          {pageHeader.flow.map((item) => {
                            const isSelected = selectedIds?.includes(item.id) ?? false;
                            const isHovered = hoveredId === item.id;
                            const isInlineEditing = inlineEditId === item.id;
                            const flowW =
                              item.flow_width && item.flow_width !== 'full'
                                ? FLOW_WIDTH_MAP[item.flow_width]
                                : undefined;
                            return (
                              <div
                                key={item.id}
                                ref={
                                  isHeaderOriginPage
                                    ? (el) => {
                                        if (el) elemRefs.current.set(item.id, el);
                                        else elemRefs.current.delete(item.id);
                                      }
                                    : undefined
                                }
                                className={
                                  inertControls(item, {fillMode, isInlineEditing, cropEditId}) ||
                                  undefined
                                }
                                style={{
                                  width: flowW,
                                  outline:
                                    canInteract && !isInlineEditing && !laysItselfOut(item)
                                      ? isSelected
                                        ? `2px solid ${SELECTION_PALETTE.flowSelected}`
                                        : isHovered
                                          ? `1.5px dashed ${SELECTION_PALETTE.flowHover}`
                                          : undefined
                                      : undefined,
                                  outlineOffset:
                                    canInteract && !isInlineEditing && (isSelected || isHovered)
                                      ? '2px'
                                      : undefined,
                                  cursor: canInteract ? 'default' : undefined,
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onMouseEnter={() => canInteract && setHoveredId(item.id)}
                                onMouseLeave={() => canInteract && setHoveredId(null)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!fillMode)
                                    onElementClick?.(item.id, {
                                      additive: e.shiftKey || e.ctrlKey || e.metaKey,
                                    });
                                }}
                                onDoubleClick={
                                  isHeaderOriginPage
                                    ? (e) => {
                                        e.stopPropagation();
                                        startInlineEdit(item);
                                      }
                                    : undefined
                                }
                              >
                                <PreviewElement
                                  item={item}
                                  fillMode={fillMode}
                                  drawFilled
                                  sampleData={sampleData}
                                  inlineEdit={isHeaderOriginPage ? inlineEdit : undefined}
                                  onChangeElement={onElementChange}
                                  onSelect={() => onElementClick?.(item.id)}
                                  {...fillProps(item.id)}
                                  cellFill={fillProps}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Absolute header items — coordinates are header-zone-relative (translated on mark-as-header) */}
                      {pageHeader.abs.map((item) =>
                        renderAbsoluteItem(item, {yOffset: 0, registerRef: isHeaderOriginPage}),
                      )}
                    </div>
                  )}

                  {/* Flow content */}
                  <div
                    style={{
                      paddingTop: marginT + headerOffset,
                      paddingBottom: marginB,
                      paddingLeft: marginL,
                      paddingRight: marginR,
                    }}
                    className="space-y-3"
                  >
                    {isEmpty && (
                      <div className="flex h-72 flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/40 text-center">
                        <LayoutTemplate className="h-9 w-9 text-gray-300" strokeWidth={1.5} />
                        <p className="text-sm font-medium text-gray-400">
                          Start designing your report
                        </p>
                        <p className="text-xs text-gray-300">
                          Add elements from the panel on the left
                        </p>
                      </div>
                    )}
                    {pageItems.map((item) => {
                      const {sentinelGroupId, inGroupEditMode, inGroupSelectionMode} =
                        groupContext(item);
                      const isSelected = selectedIds?.includes(item.id) ?? false;
                      const isHovered = hoveredId === item.id;
                      const isInlineEditing = inlineEditId === item.id;
                      const ownChrome = laysItselfOut(item);
                      const flowW =
                        item.flow_width && item.flow_width !== 'full'
                          ? FLOW_WIDTH_MAP[item.flow_width]
                          : undefined;

                      // Flow drag state
                      const dragEnabled = canEdit && !!onReorderFlow && !isInlineEditing;
                      const isDragging = flowDrag?.idSet.has(item.id) ?? false;
                      const dropBefore =
                        flowDrag?.drop?.refId === item.id && flowDrag.drop.placement === 'before';
                      const dropAfter =
                        flowDrag?.drop?.refId === item.id && flowDrag.drop.placement === 'after';

                      // Outline style: group-selection mode → dashed indigo; normal → solid/dashed
                      const outlineStyle = (() => {
                        if (!canInteract || isInlineEditing) return undefined;
                        if (ownChrome) return undefined;
                        if (inGroupSelectionMode) return `2px dashed ${SELECTION_PALETTE.selected}`;
                        if (inGroupEditMode && isSelected)
                          return `2px solid ${SELECTION_PALETTE.selected}`;
                        if (inGroupEditMode && isHovered)
                          return `1.5px dashed ${SELECTION_PALETTE.hover}`;
                        if (isSelected) return `2px solid ${SELECTION_PALETTE.selected}`;
                        if (isHovered) return `1.5px dashed ${SELECTION_PALETTE.hover}`;
                        return undefined;
                      })();

                      return (
                        <Fragment key={item.id}>
                          {dropBefore && <FlowDropIndicator />}
                          <div
                            ref={(el) => {
                              if (el) elemRefs.current.set(item.id, el);
                              else elemRefs.current.delete(item.id);
                            }}
                            className={
                              inertControls(item, {fillMode, isInlineEditing, cropEditId}) ||
                              undefined
                            }
                            style={{
                              width: flowW,
                              outline: outlineStyle,
                              outlineOffset:
                                canInteract &&
                                !isInlineEditing &&
                                (isSelected || isHovered || inGroupSelectionMode)
                                  ? '2px'
                                  : undefined,
                              cursor: dragEnabled
                                ? flowDrag
                                  ? 'grabbing'
                                  : 'grab'
                                : canInteract
                                  ? 'default'
                                  : undefined,
                              opacity: isDragging ? 0.4 : undefined,
                            }}
                            onMouseDown={(e) => startFlowDrag(e, item)}
                            onMouseEnter={() => canInteract && setHoveredId(item.id)}
                            onMouseLeave={() => canInteract && setHoveredId(null)}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!fillMode) {
                                onPageFocus?.(pageIdx);
                                if (inGroupSelectionMode) {
                                  // Route click to sentinel (triggers edit-mode on second click)
                                  onElementClick?.(sentinelGroupId!);
                                } else {
                                  onElementClick?.(item.id, {
                                    additive: e.shiftKey || e.ctrlKey || e.metaKey,
                                  });
                                }
                              }
                            }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              if (inGroupSelectionMode) {
                                // Double-click: enter group edit mode
                                onElementClick?.(sentinelGroupId!);
                              } else {
                                startEditOrCrop(item);
                              }
                            }}
                          >
                            <PreviewElement
                              item={item}
                              fillMode={fillMode}
                              drawFilled
                              sampleData={sampleData}
                              inlineEdit={inlineEdit}
                              {...fillProps(item.id)}
                              cellFill={fillProps}
                              onChangeElement={onElementChange}
                              onSelect={() => onElementClick?.(item.id)}
                              cropEditId={cropEditId}
                              zoom={zoom}
                              onCropApply={handleCropApply}
                              onCropCancel={handleCropCancel}
                            />
                          </div>
                          {dropAfter && <FlowDropIndicator />}
                        </Fragment>
                      );
                    })}
                  </div>

                  {/* Absolute group sentinel bounding boxes — shown in group-selection mode */}
                  {(absoluteSentinelsByPage.get(pageIdx) ?? []).map((sentinel) => {
                    const isSelected = selectedIds?.includes(sentinel.id) ?? false;
                    const isInGroupEditMode = groupEditId === sentinel.id;
                    if (!isSelected && !isInGroupEditMode) return null;

                    const ds = dragState?.id === sentinel.id ? dragState : null;
                    const groupOffset = dragState?.groupOffsets?.get(sentinel.id);
                    const liveX = ds?.x ?? groupOffset?.x ?? sentinel.x ?? 0;
                    const liveY = ds?.y ?? groupOffset?.y ?? sentinel.y ?? 0;
                    const localY = liveY - pageIdx * A4_H;
                    const liveW = sentinel.width ?? 100;
                    const liveH = sentinel.height ?? 100;
                    const isDraggingSentinel = !!ds || !!groupOffset;

                    return (
                      <div
                        key={sentinel.id}
                        style={{
                          position: 'absolute',
                          left: liveX,
                          top: localY,
                          width: liveW,
                          height: liveH,
                          // Below children (zIndex 10) so children still receive events;
                          // only catches clicks/drags in empty space within the bounding box.
                          zIndex: 5,
                          border: isSelected
                            ? `2px dashed ${SELECTION_PALETTE.selected}`
                            : `1px dashed ${SELECTION_PALETTE.groupBoundary}`,
                          borderRadius: 4,
                          boxSizing: 'border-box',
                          cursor:
                            canEdit && isSelected
                              ? isDraggingSentinel
                                ? 'grabbing'
                                : 'grab'
                              : 'default',
                          // In edit mode — purely visual, no pointer events.
                          pointerEvents: canEdit && isSelected ? 'auto' : 'none',
                        }}
                        onMouseDown={
                          canEdit && isSelected
                            ? (e) => {
                                e.stopPropagation();
                                startDrag(e, sentinel);
                              }
                            : undefined
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!fillMode && isSelected)
                            onElementClick?.(sentinel.id, {
                              additive: e.shiftKey || e.ctrlKey || e.metaKey,
                            });
                        }}
                      >
                        {isSelected && (
                          <span className="absolute -top-5 left-0 rounded-t bg-indigo-400 px-1.5 py-0.5 text-[9px] font-medium text-white leading-none whitespace-nowrap pointer-events-none">
                            {sentinel.label || 'Group'}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* Absolute items */}
                  {pageAbsoluteItems.map((item) =>
                    renderAbsoluteItem(item, {
                      yOffset: pageIdx * A4_H,
                      registerRef: true,
                      onBeforeDrag: () => {
                        if (!fillMode) onPageFocus?.(pageIdx);
                      },
                      onContextMenu: (e) => {
                        if (fillMode || !onReorderZ) return;
                        e.preventDefault();
                        e.stopPropagation();
                        onElementClick?.(item.id);
                        // pageAbsoluteItems is order-sorted → last = front, first = back.
                        setZMenu({
                          x: e.clientX,
                          y: e.clientY,
                          id: item.id,
                          atFront: pageAbsoluteItems[pageAbsoluteItems.length - 1]?.id === item.id,
                          atBack: pageAbsoluteItems[0]?.id === item.id,
                        });
                      },
                    }),
                  )}

                  {/* Page-number stamps. Not content on this page but a mark repeated
                      across a range of pages, so they are laid out by their own band
                      rule rather than by the flow or by coordinates. */}
                  <PageNumberStamps
                    stamps={pageNumberStamps}
                    pageIdx={pageIdx}
                    totalPages={totalPages}
                    margins={{top: marginT, bottom: marginB, left: marginL, right: marginR}}
                    canInteract={canInteract}
                    selectedIds={selectedIds}
                    hoveredId={hoveredId}
                    registerRef={(id, el) => {
                      if (el) elemRefs.current.set(id, el);
                      else elemRefs.current.delete(id);
                    }}
                    onHoverChange={setHoveredId}
                    onSelect={(id) => {
                      onPageFocus?.(pageIdx);
                      onElementClick?.(id);
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {zMenu && (
          <ElementContextMenu
            x={zMenu.x}
            y={zMenu.y}
            atFront={zMenu.atFront}
            atBack={zMenu.atBack}
            onAction={(action) => onReorderZ?.(zMenu.id, action)}
            onClose={() => setZMenu(null)}
          />
        )}
      </div>
    </FillSurfaceProvider>
  );
});
